import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, Plus, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Modal } from '@/shared/ui/Modal';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast/useToast';
import { formatDate } from '@/shared/utils/format';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { SafetyRole } from '../api/safetyApi';
import { useAddStaffMutation, useSafetyContent, useSafetyStaff } from '../hooks';
import {
  AssignmentStatusPill,
  AttestationFooter,
  PnTag,
  SafetyLoadError,
} from '../components/SafetyBits';
import styles from './StaffTrainingPage.module.css';

export function StaffTrainingPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const contentQuery = useSafetyContent();
  const { data: content } = contentQuery;
  const { data: staff, isLoading, isError, refetch } = useSafetyStaff();
  const addStaff = useAddStaffMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRoles, setNewRoles] = useState<SafetyRole[]>(['assistant']);
  const [newBbp, setNewBbp] = useState(false);

  const adminOnlyRoles: SafetyRole[] = ['front_desk', 'office_manager'];
  const isAdminOnly = newRoles.length > 0 && newRoles.every((r) => adminOnlyRoles.includes(r));

  const toggleRole = (role: SafetyRole) => {
    setNewRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handleAdd = async () => {
    try {
      await addStaff.mutateAsync({
        name: newName,
        roles: newRoles,
        bbpExposed: isAdminOnly ? newBbp : undefined,
      });
      toast.success(`${newName} added — onboarding training assigned for today`);
      setAddOpen(false);
      setNewName('');
    } catch {
      toast.error('Could not add the team member');
    }
  };

  if (isError || contentQuery.isError) {
    return (
      <div className={patterns.page}>
        <PageHeader title="Staff Training" />
        <SafetyLoadError
          onRetry={() => {
            refetch();
            contentQuery.refetch();
          }}
        />
      </div>
    );
  }

  if (isLoading || !content) {
    return (
      <div className={patterns.page}>
        <Skeleton height={80} />
        <Skeleton height={300} />
      </div>
    );
  }

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Staff Training"
        subtitle="One bundled annual course per person, tailored to their role. The quiz is what makes the training count toward OSHA’s interactive requirement."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={() => setAddOpen(true)}
          >
            Add team member
          </Button>
        }
      />

      {/* The bundled course */}
      <div className={patterns.panel}>
        <div className={styles.courseHeader}>
          <div className={styles.courseIcon}>
            <ShieldCheck size={20} aria-hidden />
          </div>
          <div className={styles.courseText}>
            <div className={styles.courseTitle}>
              {content.course.title}
              <PnTag action="Deliver" />
              <span className={patterns.pillAccent}>Annual</span>
            </div>
            <div className={styles.courseSub}>
              One course, one date, one signature — the completion record itemizes every topic for
              inspectors. 30–40 min, quiz pass ≥ {Math.round(content.course.passThreshold * 100)}%.
            </div>
            <div className={styles.topicChips}>
              {content.topics.map((t) => (
                <span key={t.id} className={styles.topicChip} title={t.citation}>
                  {t.shortTitle}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Per-person assignments */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>Assignments</span>
          <span className={patterns.sectionFootnote}>
            Topics attach automatically from each person’s role
          </span>
        </div>
        <div className={patterns.gridTable}>
          <div className={`${patterns.gridTableHeader} ${styles.staffGrid}`}>
            <span>Team member</span>
            <span>Topics</span>
            <span>Status</span>
            <span>Due</span>
            <span />
          </div>
          {(staff ?? []).map((member) => (
            <div key={member.id} className={`${patterns.gridTableRow} ${styles.staffGrid}`}>
              <div>
                <div className={patterns.cellName}>{member.name}</div>
                <div className={patterns.cellSmMuted}>{member.roleLabel}</div>
              </div>
              <span className={patterns.cellSm}>
                {member.assignment
                  ? member.assignment.topics
                      .map((id) => content.topics.find((t) => t.id === id)?.shortTitle ?? id)
                      .join(' · ')
                  : '—'}
              </span>
              <span>
                {member.assignment ? (
                  <AssignmentStatusPill status={member.assignment.status} />
                ) : (
                  '—'
                )}
              </span>
              <span className={patterns.cellMuted}>
                {member.assignment ? formatDate(member.assignment.dueDate) : '—'}
              </span>
              <span className={styles.rowAction}>
                {member.assignment &&
                  (member.assignment.status === 'complete' ? (
                    <span className={patterns.cellSmMuted}>
                      {Math.round((member.assignment.quizScore ?? 0) * 100)}% · e-signed
                    </span>
                  ) : (
                    <button
                      className={styles.takeLink}
                      onClick={() => navigate(`/safety/training/${member.assignment?.id}/take`)}
                    >
                      Take training <ArrowRight size={12} aria-hidden />
                    </button>
                  ))}
              </span>
            </div>
          ))}
          {(staff ?? []).length === 0 && (
            <div className={patterns.gridTableEmpty}>
              No team members yet — add your first above.
            </div>
          )}
        </div>
      </section>

      {/* Occasional — Track / Refer items, never a course */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>Occasional & credentials</span>
          <span className={patterns.sectionFootnote}>
            Issued elsewhere — we point you to the provider and track the deadline
          </span>
        </div>
        <div className={patterns.panel}>
          <div className={styles.occList}>
            {content.occasionalItems.map((item) => (
              <div key={item.id} className={styles.occRow}>
                <div className={styles.occBody}>
                  <div className={styles.occTitle}>
                    <span>{item.title}</span>
                    <PnTag action={item.pnAction} />
                  </div>
                  <div className={styles.occDetail}>{item.detail}</div>
                  <div className={styles.occDisclaimer}>{item.disclaimer}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.noCeNote}>
        <GraduationCap size={14} aria-hidden />
        <span>{content.copy.noCeNote}</span>
      </div>

      <AttestationFooter text={content.copy.attestation} />

      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add a team member"
        size="sm"
        footer={
          <div className={styles.modalFooter}>
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAdd}
              disabled={newName.trim().length === 0 || newRoles.length === 0}
              loading={addStaff.isPending}
            >
              Add & assign training
            </Button>
          </div>
        }
      >
        <div className={patterns.fieldStack}>
          <Input
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Sara Kim"
            fullWidth
          />
          <div>
            <div className={styles.roleCheckboxLabel}>Role(s)</div>
            <div className={styles.roleCheckboxGroup}>
              {content.roles.map((r) => (
                <label key={r.value} className={styles.bbpToggle}>
                  <input
                    type="checkbox"
                    checked={newRoles.includes(r.value)}
                    onChange={() => toggleRole(r.value)}
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          {isAdminOnly && (
            <label className={styles.bbpToggle}>
              <input
                type="checkbox"
                checked={newBbp}
                onChange={(e) => setNewBbp(e.target.checked)}
              />
              Duties involve handling contaminated items (adds Bloodborne Pathogens)
            </label>
          )}
          <p className={styles.modalHint}>
            New hires get an onboarding assignment due today, then join the practice’s annual cycle.
          </p>
        </div>
      </Modal>
    </div>
  );
}
